import { useCallback, useRef, useState } from 'react';
import { UnsavedChangesDialog } from '@/components/custom/unsaved-changes-dialog';

export interface DiscardGuard {
  /** The editor reports whether it holds unsaved changes. */
  setDirty: (isDirty: boolean) => void;
  /** Runs `leave` at once when nothing is unsaved, else once the user agrees to discard. */
  guard: (leave: () => void) => void;
  /** The confirmation; render it once beside the sheet it guards. */
  dialog: React.ReactElement;
}

/**
 * Guards leaving an editor that may hold unsaved changes — a sheet's X,
 * Escape, overlay click or Cancel — with the app's own discard dialog.
 */
export function useDiscardGuard(): DiscardGuard {
  const dirty = useRef(false);
  const [pending, setPending] = useState<(() => void) | null>(null);

  const setDirty = useCallback((isDirty: boolean): void => {
    dirty.current = isDirty;
  }, []);

  const guard = useCallback((leave: () => void): void => {
    if (dirty.current) setPending(() => leave);
    else leave();
  }, []);

  const dialog = (
    <UnsavedChangesDialog
      open={pending !== null}
      onCancel={() => setPending(null)}
      onDiscard={() => {
        setPending(null);
        dirty.current = false;
        pending?.();
      }}
    />
  );

  return { setDirty, guard, dialog };
}
